import Link from 'next/link'
import clsx from 'clsx'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { HoverErrors } from './monitors/HoverErrors'

type ButtonColor = 'sky' | 'white' | 'red'
export const getColorClassName = (color: ButtonColor, enabled: boolean) => {
  return {
    sky: {
      enabled: 'bg-sky-400 text-white hover:bg-sky-500 focus:ring-sky-500',
      disabled: 'bg-gray-400 text-white',
    },
    white: {
      enabled:
        'bg-white text-gray-900 focus:ring-sky-500 ring-gray-300 ring-1 hover:text-sky-500',
      disabled: 'bg-white text-gray-500 ring-gray-300 ring-1',
    },
    red: {
      enabled: 'bg-red-500 text-white hover:bg-red-600',
      disabled: 'bg-red-400 text-white',
    },
  }[color][enabled ? 'enabled' : 'disabled']
}

type ActionButtonProps = {
  href?: string
  enabled?: boolean
  onClick?: () => void | Promise<void>
  color?: 'sky' | 'white' | 'red'
  className?: string
  width?: string
  loading?: boolean
  openInNewTab?: boolean
  padding?: 'normal' | 'small' | 'tiny'
  title?: string
  children: React.ReactNode
  hoverErrors?: string
  presentDisabled?: boolean
  classes?: {
    root?: string
    button?: string
  }
  buttonProps?: React.ComponentProps<'button'>
}

export function ActionButton({
  href,
  enabled = true,
  onClick,
  color = 'sky',
  className = '',
  width = '',
  loading = false,
  openInNewTab = false,
  padding = 'normal',
  title = '',
  hoverErrors,
  children,
  presentDisabled = false,
  classes,
  buttonProps,
}: ActionButtonProps) {
  if (loading && !width) {
    throw new Error('width must be specified when loading is true')
  }

  if (href && onClick) {
    throw new Error('href and onClick cannot both be specified')
  }

  const colorClassEnabled = presentDisabled ? false : enabled
  const colorClassName = getColorClassName(color, colorClassEnabled)

  function Button() {
    return (
      <button
        className={clsx(
          'flex h-full w-full items-center justify-center rounded-md font-medium',
          padding == 'tiny' ? 'text-xs' : 'text-sm',
          colorClassName,
          padding == 'normal' && 'px-4',
          padding == 'small' && 'px-2.5',
          padding == 'tiny' && 'px-2',
          !enabled && 'cursor-not-allowed',
          className,
          classes?.button
        )}
        onClick={enabled && !href ? onClick : () => {}}
        title={title}
        {...buttonProps}
      >
        {children}
      </button>
    )
  }

  return (
    <div
      className={clsx(
        padding == 'normal' && 'h-10',
        padding == 'small' && 'h-[2.25rem]',
        padding == 'tiny' && 'h-[1.75rem]',
        classes?.root,
        width
      )}
    >
      {loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingSpinner svgClassName="h-8" />
        </div>
      ) : (
        <>
          {href && (
            <Link
              href={href}
              {...(openInNewTab
                ? { rel: 'noopener noreferrer', target: '_blank' }
                : {})}
              onClick={(e) => {
                e.stopPropagation()
              }} // prevents parent container onClicks from firing
              title={title}
            >
              <Button />
            </Link>
          )}
          {onClick && (
            <HoverErrors className="flex h-full w-full" error={hoverErrors}>
              <Button />
            </HoverErrors>
          )}
        </>
      )}
    </div>
  )
}
